import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
  useInvoiceDetailsQuery,
  useSetInvoiceDetailsLegalMutation,
} from "@smp/graphql";
import { UiButton, UiGrid, UiSpinner, UiTypography } from "@smp/ui";
import { Formik, FormikConfig } from "formik";
import { useSnackbar } from "notistack";
import { useCallback } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { FormErrors } from "@/components/form/form_errors";
import { FormTextField } from "@/components/form/form_text_field";
import { gqlClient } from "@/hooks/use_graphql";

import { IFormValues } from "./form_legal.types";

export const FormLegal = ({
  isModal,
  onCancel,
}: {
  isModal?: boolean;
  onCancel?: () => void;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const queryClient = useQueryClient();
  const { data: invoiceDetailsData, isLoading: isInvoiceDetailsDataLoading } =
    useInvoiceDetailsQuery(gqlClient);

  const invoiceLegalData = invoiceDetailsData?.invoice_details.legal_entity;

  const { mutateAsync: mutateSetInvoiceDetailsLegal } =
    useSetInvoiceDetailsLegalMutation(gqlClient);

  const handleSubmit: FormikConfig<IFormValues>["onSubmit"] = useCallback(
    async (values, { setStatus, setSubmitting }) => {
      mutateSetInvoiceDetailsLegal(
        {
          input: {
            address: values.address,
            city: values.city,
            country: values.country,
            first_name: values.first_name,
            last_name: values.last_name,
            organization_name: values.organization_name,
            postal_code: values.postal_code,
            vat_id: values.vat_id,
          },
        },
        {
          onError: (fail) => {
            setStatus(fail);
          },
          onSuccess: () => {
            enqueueSnackbar(
              <UiTypography>
                <Trans>Invoice details have been changed successfully</Trans>
              </UiTypography>,
              {
                variant: "success",
              },
            );
            queryClient.invalidateQueries(useInvoiceDetailsQuery.getKey());
            onCancel ? onCancel() : navigate("/settings");
          },
        },
      );
      setStatus();
      setSubmitting(false);
    },
    [
      enqueueSnackbar,
      mutateSetInvoiceDetailsLegal,
      navigate,
      onCancel,
      queryClient,
    ],
  );

  return isInvoiceDetailsDataLoading ? (
    <UiSpinner title={<Trans>Loading</Trans>} />
  ) : (
    <Formik<IFormValues>
      initialValues={{
        address: invoiceLegalData?.address || "",
        city: invoiceLegalData?.city || "",
        country: invoiceLegalData?.country || "",
        first_name: invoiceLegalData?.first_name || "",
        last_name: invoiceLegalData?.last_name || "",
        organization_name: invoiceLegalData?.organization_name || "",
        postal_code: invoiceLegalData?.postal_code || "",
        vat_id: invoiceLegalData?.vat_id || "",
      }}
      onSubmit={handleSubmit}
      validationSchema={Yup.object().shape({
        address: Yup.string()
          .trim()
          .required(i18n._(t`This field cannot be blank`))
          .max(255, i18n._(t`Max. length is ${255} characters`))
          .min(1, i18n._(t`Min. length is ${1} characters`)),
        city: Yup.string()
          .trim()
          .required(i18n._(t`This field cannot be blank`))
          .max(30, i18n._(t`Max. length is ${30} characters`))
          .min(1, i18n._(t`Min. length is ${1} characters`)),
        country: Yup.string()
          .trim()
          .required(i18n._(t`This field cannot be blank`))
          .max(30, i18n._(t`Max. length is ${30} characters`))
          .min(1, i18n._(t`Min. length is ${1} characters`)),
        first_name: Yup.string()
          .trim()
          .max(30, i18n._(t`Max. length is ${30} characters`)),
        last_name: Yup.string()
          .trim()
          .max(30, i18n._(t`Max. length is ${30} characters`)),
        organization_name: Yup.string()
          .trim()
          .required(i18n._(t`This field cannot be blank`))
          .max(255, i18n._(t`Max. length is ${255} characters`))
          .min(1, i18n._(t`Min. length is ${1} characters`)),
        postal_code: Yup.string()
          .trim()
          .required(i18n._(t`This field cannot be blank`))
          .max(30, i18n._(t`Max. length is ${30} characters`))
          .min(1, i18n._(t`Min. length is ${1} characters`)),
        vat_id: Yup.string()
          .trim()
          .required(i18n._(t`This field cannot be blank`))
          .max(30, i18n._(t`Max. length is ${30} characters`))
          .min(1, i18n._(t`Min. length is ${1} characters`)),
      })}
    >
      {({ dirty, handleSubmit, isSubmitting }) => (
        <form onSubmit={handleSubmit}>
          <FormErrors />
          <UiGrid container spacing={2}>
            <UiGrid
              item
              xs={isModal ? 6 : 12}
              style={{ order: isModal ? 1 : 0 }}
            >
              <FormTextField
                fullWidth
                gutterBottom
                label={i18n._(t`Company name`) + " *"}
                name="organization_name"
              />
            </UiGrid>
            <UiGrid
              item
              xs={isModal ? 6 : 12}
              style={{ order: isModal ? 3 : 1 }}
            >
              <FormTextField
                fullWidth
                gutterBottom
                label={i18n._(t`Name`)}
                name="first_name"
              />
            </UiGrid>
            <UiGrid
              item
              xs={isModal ? 6 : 12}
              style={{ order: isModal ? 5 : 2 }}
            >
              <FormTextField
                fullWidth
                gutterBottom
                label={i18n._(t`Surname`)}
                name="last_name"
              />
            </UiGrid>
            <UiGrid
              item
              xs={isModal ? 6 : 12}
              style={{ order: isModal ? 7 : 3 }}
            >
              <FormTextField
                fullWidth
                gutterBottom
                label={i18n._(t`VAT ID`) + " *"}
                name="vat_id"
              />
            </UiGrid>
            <UiGrid
              item
              xs={isModal ? 6 : 12}
              style={{ order: isModal ? 2 : 4 }}
            >
              <FormTextField
                fullWidth
                gutterBottom
                label={i18n._(t`Street \ House number`) + " *"}
                name="address"
              />
            </UiGrid>
            <UiGrid
              item
              xs={isModal ? 6 : 12}
              style={{ order: isModal ? 4 : 5 }}
            >
              <FormTextField
                fullWidth
                gutterBottom
                label={i18n._(t`Postal code`) + " *"}
                name="postal_code"
              />
            </UiGrid>
            <UiGrid
              item
              xs={isModal ? 6 : 12}
              style={{ order: isModal ? 6 : 6 }}
            >
              <FormTextField
                fullWidth
                gutterBottom
                label={i18n._(t`City`) + " *"}
                name="city"
              />
            </UiGrid>
            <UiGrid
              item
              xs={isModal ? 6 : 12}
              style={{ order: isModal ? 8 : 7 }}
            >
              <FormTextField
                fullWidth
                gutterBottom
                label={i18n._(t`Country`) + " *"}
                name="country"
                style={{ marginBottom: 8 }}
              />
            </UiGrid>

            <UiGrid item xs={12} style={{ order: isModal ? 10 : 10 }}>
              <UiButton
                color="primary"
                disabled={!dirty}
                gutterRight
                isLoading={isSubmitting}
                type="submit"
                variant="contained"
              >
                <Trans>Save</Trans>
              </UiButton>
              <UiButton onClick={onCancel ? onCancel : () => navigate(-1)}>
                <Trans>Cancel</Trans>
              </UiButton>
            </UiGrid>
          </UiGrid>
        </form>
      )}
    </Formik>
  );
};
